import { React } from 'Imports';
import { useEffect, useState } from 'react';
import { ConfigService } from '$State/ConfigFreezerService';
import SingleSelectDropdownMenu from '$Components/Shared/Dropdown/SingleSelectDropdownMenu';
import { StyledPlainSubmitButton } from '$Components/VideoEvents/VideoEventList/VideoEventListStyles';
import { FleetSettingsService, IFleetSettingsServiceInjectedProps } from '$State/FleetSettingsFreezerService';
import { useAppSnackbar } from '$Utilities/hooks/useAppSnackbar';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';

const styles = require('./VideoOverlayTimezone.scss') as {
    container: string;
    heading: string;
    description: string;
    descriptionRow: string;
    dropdownWrapper: string;
};

interface IVideoOverlayTimezoneProps {
    onDirtyStateChange: (isDirty: boolean) => void;
    resetSignal?: number;
}

type VideoOverlayTimezoneProps = IVideoOverlayTimezoneProps & IFleetSettingsServiceInjectedProps;

const TIMEZONE_OPTIONS = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'America/Adak',
    'Pacific/Honolulu',
];

const getUtcOffset = (timezone: string): string => {
    const now = new Date();
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / 60000;
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(offsetMinutes);
    const hours = Math.floor(absMinutes / 60);
    const minutes = Math.round(absMinutes % 60);
    return minutes === 0 ? `UTC${sign}${hours}` : `UTC${sign}${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
};

const DEFAULT_TIMEZONE = 'America/Chicago';

const _VideoOverlayTimezone = (props: VideoOverlayTimezoneProps) => {
    const { showSnackbarMessage } = useAppSnackbar();
    const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
    const [timezoneValue, setTimezoneValue] = useState(DEFAULT_TIMEZONE);
    const [savedTimezone, setSavedTimezone] = useState(DEFAULT_TIMEZONE);
    const [disableSubmit, setDisableSubmit] = useState(false);

    const hasUnsavedChanges = (current: string, saved: string) => current !== saved;

    const timezoneItems = TIMEZONE_OPTIONS.map((tz) => ({
        label: `${tz} (${getUtcOffset(tz)})`,
        value: tz,
        onClick: () => setTimezoneValue(tz),
    }));

    const fetchData = async () => {
        await props.FleetSettings.getVideoOverlayTimezone();
        const { getVideoOverlayTimezone } = props.FleetSettings.getState();
        if (getVideoOverlayTimezone.data?.timezone) {
            const tz = getVideoOverlayTimezone.data.timezone;
            setTimezoneValue(tz);
            setSavedTimezone(tz);
        }
    };

    useEffect(() => {
        fetchData();
    }, [props.resetSignal]);

    useEffect(() => {
        props.onDirtyStateChange(hasUnsavedChanges(timezoneValue, savedTimezone));
    }, [timezoneValue, savedTimezone]);

    const handleSaveClick = async () => {
        setDisableSubmit(true);
        await props.FleetSettings.saveVideoOverlayTimezone(timezoneValue);
        const { saveVideoOverlayTimezone } = props.FleetSettings.getState();
        if (saveVideoOverlayTimezone.error) {
            showSnackbarMessage({ message: 'Settings unable to be saved.', alertType: AlertTypes.Fail });
        } else {
            setSavedTimezone(timezoneValue);
            showSnackbarMessage({ message: 'Settings saved successfully.', alertType: AlertTypes.Success });
        }
        setDisableSubmit(false);
    };

    return (
        <div>
            <div className={styles.container}>
                <div className={styles.heading}>Video Overlay Time Zone</div>
                <div className={styles.descriptionRow}>
                    <span className={styles.description}>Set the time zone that will appear on video overlays.</span>
                    <div className={styles.dropdownWrapper}>
                        <SingleSelectDropdownMenu
                            onClose={() => setDropdownIsOpen(false)}
                            onOpen={() => setDropdownIsOpen(true)}
                            items={timezoneItems}
                            open={dropdownIsOpen}
                            searchProps={undefined}
                            required={true}
                            defaultValue={timezoneValue}
                            sortItems={false}
                        />
                    </div>
                </div>
                <StyledPlainSubmitButton
                    onClick={handleSaveClick}
                    disabled={disableSubmit || !hasUnsavedChanges(timezoneValue, savedTimezone)}
                >
                    Save Changes
                </StyledPlainSubmitButton>
            </div>
        </div>
    );
};

export const VideoOverlayTimezone = ConfigService.inject(FleetSettingsService.inject(_VideoOverlayTimezone));
